import { useRef, useState } from "react";
import { buildRequestPayload } from "@/lib/request-payload";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitRequest } from "@/lib/cms.functions";
import { trackLead } from "@/lib/meta-pixel";
import { useLocale } from "@/hooks/useLocale";
import { useT } from "@/lib/i18n-dict";
import { LOCALE_DEFAULT_COUNTRY } from "@/lib/countries";
import { validateWorkEmail } from "@/lib/email-domains";
import { CountrySelect } from "./CountrySelect";
import { IsoIllustration } from "./Iso";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listContent } from "@/lib/cms.functions";

/** Broader manufacturing sectors shown in request forms beyond the Industries section. */
export const EXTRA_INDUSTRIES = [
  "Metalworking & machining",
  "Machine building",
  "Plastics & polymers",
  "Electronics & PCB assembly",
  "Automotive components",
  "Furniture & woodworking",
  "Food & beverage",
  "Packaging",
  "Textile & apparel",
  "Chemicals",
  "Pharmaceuticals",
  "Construction materials",
  "Electrical equipment",
] as const;


type Kind = "demo" | "contact" | "partner";

export function RequestForm({
  kind,
  submitLabel,
  successTitle,
  successText,
  withMessage = false,
  defaultMessage = "",
  defaultIndustry = "",
  hideIndustry = false,
  planSlug = "",
}: {
  kind: Kind;
  submitLabel: string;
  successTitle: string;
  successText: string;
  withMessage?: boolean;
  /** Pre-filled text for the message field (e.g. the add-on the visitor came from). */
  defaultMessage?: string;
  /** Pre-selected industry when the visitor already chose it in another flow. */
  defaultIndustry?: string;
  /** Avoid repeating the industry field when it is visible in the surrounding summary. */
  hideIndustry?: boolean;
  /** Plan slug selected in the solution finder; price is resolved server-side for the current locale. */
  planSlug?: string;
}) {
  const locale = useLocale();
  const t = useT();
  const send = useServerFn(submitRequest);
  const loadIndustries = useServerFn(listContent);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const submission = useRef<{ id: string; fingerprint: string } | null>(null);
  const [country, setCountry] = useState<string>(LOCALE_DEFAULT_COUNTRY[locale] ?? "");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [industry, setIndustry] = useState(defaultIndustry);
  const [industryError, setIndustryError] = useState<string | null>(null);
  const { data: industries = [] } = useQuery({
    queryKey: ["request-form-industries", locale],
    queryFn: () => loadIndustries({ data: { kind: "industry", locale, limit: 100 } }),
  });

  if (done) {
    return (
      <div className="rounded-2xl border border-primary/30 bg-surface p-8 text-center">
        <IsoIllustration
          name="sendMessage"
          alt={t("Request sent from a laptop")}
          className="mx-auto w-full max-w-[240px]"
          parallax={false}
        />
        <h2 className="mt-5 font-display text-2xl font-semibold">{t(successTitle)}</h2>
        <p className="mt-3 text-sm text-muted-foreground">{t(successText)}</p>
      </div>
    );
  }


  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const email = String(form.get("email") ?? "").trim().toLowerCase();
    const fullName = String(form.get("fullName") ?? "").trim();

    const emailProblem = validateWorkEmail(email);
    const nameProblem = fullName ? null : t("Enter your full name.");
    const countryProblem = country ? null : t("Select your country.");
    const industryProblem = industry ? null : t("Select your industry.");
    setEmailError(emailProblem);
    setNameError(nameProblem);
    setCountryError(countryProblem);
    setIndustryError(industryProblem);
    if (emailProblem || nameProblem || countryProblem || industryProblem) return;

    const payload = buildRequestPayload({
      kind,
      fullName,
      email,
      company: String(form.get("company") ?? ""),
      phone: String(form.get("phone") ?? ""),
      country,
      industry,
      message: String(form.get("message") ?? ""),
      planSlug,
      website: String(form.get("website") ?? ""),
      locale,
    });
    // Same UUID for every retry of identical data; a new one only when the data changes.
    const fingerprint = JSON.stringify(payload);
    if (!submission.current || submission.current.fingerprint !== fingerprint) {
      submission.current = { id: crypto.randomUUID(), fingerprint };
    }

    inFlight.current = true;
    setPending(true);
    try {
      await send({ data: { ...payload, submissionId: submission.current.id } });
      submission.current = null;
      formEl.reset();
      setDone(true);
      trackLead();
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? t(error.message)
          : t("We could not submit your request right now. Please try again."),
      );
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-border bg-card p-6 md:p-8"
      noValidate
    >
      <div className="grid items-start gap-x-5 gap-y-5 sm:grid-cols-2">
        <Field
          id="fullName"
          label={t("Full name")}
          required
          autoComplete="name"
          placeholder={t("John Smith")}
          error={nameError}
          onBlurValue={(value) => setNameError(value.trim() ? null : t("Enter your full name."))}
        />
        <Field
          id="email"
          label={t("Work email")}
          type="email"
          required
          autoComplete="email"
          placeholder="name@company.com"
          error={emailError ? t(emailError) : null}
          onBlurValue={(value) => setEmailError(value ? validateWorkEmail(value) : null)}
        />
        <Field
          id="company"
          label={t("Company")}
          autoComplete="organization"
          placeholder={t("Acme Manufacturing Ltd.")}
        />
        {!hideIndustry ? (
          <div className="grid gap-2">
            <Label htmlFor="industry">
              {t("Industry")}
              <span className="text-primary"> *</span>
            </Label>
            <Select
              value={industry}
              onValueChange={(value) => {
                setIndustry(value);
                setIndustryError(null);
              }}
            >
              <SelectTrigger id="industry" aria-invalid={industryError ? true : undefined}>
                <SelectValue placeholder={t("Select your industry")} />
              </SelectTrigger>
              <SelectContent>
                {industries.map((item) => (
                  <SelectItem key={item.id} value={item.title}>
                    {item.title}
                  </SelectItem>
                ))}
                {EXTRA_INDUSTRIES.length ? (
                  <>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>{t("Other industries")}</SelectLabel>
                      {EXTRA_INDUSTRIES.map((sector) => (
                        <SelectItem key={sector} value={sector}>
                          {t(sector)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </>
                ) : null}
                <SelectItem value="Other manufacturing">{t("Other manufacturing")}</SelectItem>
              </SelectContent>
            </Select>
            {industryError ? <p className="text-sm text-destructive">{industryError}</p> : null}
          </div>
        ) : null}
        <div className="grid gap-2">
          <Label htmlFor="country">
            {t("Country")}
            <span className="text-primary"> *</span>
          </Label>
          <CountrySelect
            value={country}
            invalid={Boolean(countryError)}
            onChange={(code) => {
              setCountry(code);
              setCountryError(null);
            }}
          />
          {countryError ? (
            <p className="text-sm text-destructive">{countryError}</p>
          ) : null}
        </div>
        <Field id="phone" label={t("Phone")} type="tel" autoComplete="tel" placeholder="+380 67 123 4567" />
      </div>

      {withMessage ? (
        <div className="mt-5 grid gap-2">
          <Label htmlFor="message">{t("What would you like to see?")}</Label>
          <Textarea
            id="message"
            name="message"
            rows={4}
            maxLength={4000}
            key={defaultMessage}
            defaultValue={defaultMessage}
            placeholder={t("For example: planning for two production lines, warehouse traceability…")}
          />
        </div>
      ) : null}

      {/* Honeypot — hidden from humans */}
      <div className="hidden" aria-hidden>
        <label htmlFor="website">Website</label>
        <input id="website" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      <Button type="submit" size="lg" className="mt-7 w-full sm:w-auto" disabled={pending}>
        {pending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
        {t(submitLabel)}
      </Button>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        {t("By submitting you agree that we may contact you about factory.online.")}
      </p>
    </form>
  );
}

function Field({
  id,
  label,
  type = "text",
  required,
  autoComplete,
  placeholder,
  error,
  onBlurValue,
}: {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  error?: string | null;
  onBlurValue?: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>
        {label}
        {required ? <span className="text-primary"> *</span> : null}
      </Label>
      <Input
        id={id}
        name={id}
        type={type}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={error ? "border-destructive" : undefined}
        onBlur={onBlurValue ? (event) => onBlurValue(event.currentTarget.value) : undefined}
      />
      {error ? (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
